<template>
  <div class="vcl-logon--container" :style="logonStyles">
    <div class="vcl-logon">
      <img class="vcl-logon__logo" :src="logo" :alt="name">
      <slot name="controls" />
      <div class="vcl-logon__container">
        <div v-if="isBusy" class="vcl-logon__loader">
          <VpIcon class="vcl-logon__loader-icon" icon="loading" spin />
          <h3 class="vcl-logon__title">{{ $t('loading') }}...</h3>
          <p class="vcl-logon__description vcl-logon__description--center">
            {{ $t('computeCapacityLineOne') }}<br>
            {{ $t('computeCapacityLineTwo') }}
          </p>
        </div>
        <div v-show="!isBusy">
          <component v-if="!authenticated" :is="page" :styles="generalStyles" />
        </div>
      </div>
      <div v-if="page === 'reset-password'" class="vcl-logon__dark-overlay"></div>
    </div>
    <div v-if="iframePromo" class="vcl-logon__iframe-promo">
      <iframe :src="iframePromo" scrolling="no" />
    </div>
  </div>
</template>

<script>
import $color from '../../utilities/common/filter/color';

// General Components
import VpIcon from '../../components/vp-icon/VpIcon.vue';

// Logon Components
import VpAuth from './VpAuth.vue';
import VpLogon from './VpLogon.vue';
import VpLogonSuccess from './VpLogonSuccess.vue';
import VpLogonError from './VpLogonError.vue';
import VpResetPassword from './VpResetPassword.vue';
import VpTotp from './VpTotp.vue';
import VpAuthProxy from './VpAuthProxy.vue';
import VpLinkHouse from './VpLinkHouse.vue';

export default {
  name: 'VpAuthentication',
  components: {
    VpIcon,
    VpAuth,
    VpLogon,
    VpLogonSuccess,
    VpLogonError,
    VpResetPassword,
    VpTotp,
    VpAuthProxy,
    VpLinkHouse,
  },
  provide() {
    const root = this;
    const $screen = {
      width: 0,
      height: 0,
    };
    Object.defineProperty($screen, 'width', {
      enumerable: true,
      get: () => this.window.width,
    });

    Object.defineProperty($screen, 'height', {
      enumerable: true,
      get: () => this.window.height,
    });

    return {
      $busy: {
        on: (value) => { root.busy = value; },
      },
      $auth: {
        set: (value) => {
          root.authenticated = value;
          root.$emit('is-authenticated', value);
        },
      },
      $color,
      $screen,
    };
  },
  props: {
    logo: {
      type: String,
      default() {
        return require('../../../assets/vp-logo.png');
      },
    },
    name: {
      type: String,
      default: 'Virtual Peaker',
    },
    iframePromo: {
      type: String,
      default: null,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  emits: ['busy', 'is-authenticated'],
  data() {
    return {
      busy: false,
      isPasswordVisible: false,
      authenticated: false,
      page: 'VpLogon',
      window: {
        width: 0,
        height: 0,
      }
    };
  },
  computed: {
    isBusy() {
      return this.busy;
    },
    logonStyles() {
      return {
        '--links-color': this.styles.primaryColor || this.styles.linksColor || '#F58220',
        '--background-color': this.styles.primaryColor || this.styles.backgroundColor || '#1B3664',
      };
    },
    generalStyles() {
      return {
        ...this.styles,
        checkbox: {
          size: 20,
          color: this.styles.primaryColor || this.styles.checkboxColor || '#016699',
        },
        input: {
          focusColor: this.styles.primaryColor || this.styles.inputFocusColor || '#016699',
        },
        button: {
          color: this.styles.buttonColor || '#fff',
          backgroundColor: this.styles.primaryColor || this.styles.buttonBackgroundColor || '#F58220',
          border: `solid 1px ${this.styles.primaryColor || this.styles.buttonBackgroundColor || '#F58220'}`,
          focusBorder: `solid 1px ${$color.lighten(this.styles.primaryColor, 50) || this.styles.buttonBackgroundColor || '#F58220'}`,
          disabledBackgroundColor: this.styles.primaryColor || this.styles.buttonBackgroundColor || '#F58220',
        }
      };
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to = {}) {
        const { path = null } = to;

        switch (path) {
          case '/logon':
            this.page = 'VpLogon';
            break;
          case '/logon/success':
            this.page = 'VpLogonSuccess';
            break;
          case '/logon/error':
            this.page = 'VpLogonError';
            break;
          case '/logon/password-reset':
            this.page = 'VpResetPassword';
            break;
          case '/auth':
            this.page = 'VpAuth';
            break;
          case '/totp':
            this.page = 'VpTotp';
            break;
          case '/auth-proxy':
            this.page = 'VpAuthProxy';
            break;
          case '/link-house':
            this.page = 'VpLinkHouse';
            break;
          default:
            this.page = 'VpLogon';
            break;
        }
      }
    },
  },
  created() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (!['/auth', '/totp', '/auth-proxy', '/link-house'].some((path) => path === this.$route.path)) {
        if (this.app === 'utility') {
          localStorage.setItem('directLinkUrl', window.location.href || '');
        }
        this.refresh();
      }
      const check = async () => {
        if (this.authenticated) {
          await this.refresh();
        }
        setTimeout(check, 4.5 * 60 * 1000);
      };
      setTimeout(check, 4.5 * 60 * 1000); // 4.5 minutes
    },
    refresh() {
      this.busy = true;
      const { logon } = this.$deps;
      return logon.refresh()
        .then((data) => {
          const { authenticated = false } = data;

          this.authenticated = authenticated;
          this.$emit('is-authenticated', authenticated);
        })
        .catch(() => {
          this.authenticated = false;
          this.$emit('is-authenticated', false);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onResize() {
      this.window.height = window.innerHeight;
      this.window.width = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/mixins.scss";

/******* START: LOGON STYLES ********/
  .vcl-logon--container {
    background-color: var(--background-color);
    position: relative;
    width: 100vw;
    display: flex;
    overflow-y: auto;
    margin: 0;
    padding: 0;
  }
  .vcl-logon {
    $self: &;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100vw;
    padding-bottom:32px;
    min-height:100vh;
    height: fit-content;
    padding-bottom:24px;
    background-color: var(--background-color);
    &__logo {
      margin: 32px 0px 32px;
      max-height: 85px;
      max-width: 287px !important;
    }
    &__loader {
      height: 480px;
    }

    &__container {
      align-items: center;
      background-color: #fff;
      border-radius: 4px;
      display: flex;
      justify-content: center;
      padding: 0;
      position: relative;
      width: 95%;
      min-height: 552px;
      z-index: 4;
      @include Tablet {
        width: 444px;
      }
      &::v-deep .mb19 {
        margin-bottom: 19px !important;
      }
      &::v-deep #{ $self }__link {
        cursor: pointer;
        text-decoration-line: underline;
        color: var(--links-color);
        white-space: nowrap;
      }

      &::v-deep #{ $self }__title {
        font-size: 32px;
        font-weight: 350;
        line-height: 1;
        margin: 10px 0 10px;
        text-align: center;
        color: #40454A;
        padding: 12px 4px;
        text-align: center;
      }

      &::v-deep #{ $self }__description {
        margin: 0;
        margin-bottom: 17px;
        padding: 0;
        font-size: 16px;
        line-height: 24px;
        color: #4F4F4F;
        &--center {
          text-align: center;
        }
      }

      &::v-deep #{ $self }__button {
        left: 50%;
        transform: translateX(-50%);
        transition: none;
        width: 90%;
        @include Tablet {
          width: 350px;
        }
      }

      &::v-deep #{ $self }__rounded-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #A7CCE1;
        width: 100px;
        height: 100px;
        border-radius: 50%;
        margin-bottom: 20px;
        .rounded-icon {
          &__email {
            margin-top: 5px;
            font-size: 47px;
            color: #fff;
          }
          &__error {
            font-size: 47px;
            color: #fff;
          }
        }
      }

      &::v-deep #{ $self }__close-button {
        position: absolute;
        padding: 0;
        margin: 0;
        top: 22px;
        right: 23px;
        color: #AFAFAF;
        font-size: 13px;
      }

      #{ $self }__reset-password {
        padding: 45px 47px 32px;
        @include Mobile--Only {
          padding: 24px 17px;
        }
      }

      #{ $self }__loader {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        &-icon {
          font-size: 102px;
          color: #8C9196;
          margin-right: 5px;
        }
        h3 {
          margin-top: 35px;
        }
      }

      .c-logon__logon-error-message {
        position: relative;
        span {
          font-family: Muli;
          font-style: normal;
          font-weight: normal;
          font-size: 12px;
          line-height: 15px;
          color: #CE3C31;
          position: absolute;
          top: 6px;
          left: 0;
        }

      }
    }

    &__dark-overlay {
      position: absolute;
      content: '';
      top: 0;
      height: 100%;
      left: 0;
      right: 0;
      z-index: 1;
      background: rgba(0, 0, 0, 0.5);
      z-index: 3;
    }
  }
  .vcl-logon__iframe-promo {
    min-height: 100vh;
    width: 88%;
    overflow: hidden;
    padding: 0;
    margin: 0;
    iframe  {
      width: 100%;
      height: 100vh;
      overflow: hidden !important;
      margin: 0;
      padding: 0;
      border: none;
    }

    @media screen and (max-width: 1024px) {
      display: none;
    }
  }
/******* END: LOGON STYLES ********/
</style>
